import { displayLabel } from '../../utils/displayLabel';

export const unwrap = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    if ("S" in value) return value.S;
    if ("BOOL" in value) return value.BOOL;
    if ("SS" in value) return value.SS;
    if ("L" in value) return value.L.map(unwrap);
    if ("M" in value) return Object.fromEntries(Object.entries(value.M).map(([k, v]) => [k, unwrap(v)]));
  }
  return value;
};
const values = (value) => {
  const plain = unwrap(value);
  if (Array.isArray(plain)) return plain.flatMap(values);
  if (typeof plain === "string") return plain.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
  if (plain && typeof plain === "object") return Object.values(plain).flatMap(values);
  return [];
};
export const notificationId = (item) => JSON.stringify([
  unwrap(item.notificationId || item.id || item.PK), unwrap(item.SK || item.createdAt),
  unwrap(item.authorSub || item.authorId || item.authorName), unwrap(item.title), unwrap(item.link),
]);
export const notificationTime = (item) => Date.parse(unwrap(item.createdAt || item.SK)) || 0;
export const isNotificationRead = (item, readIds) => item.notificationCopies ? item.notificationCopies.every(copy => isNotificationRead(copy, readIds)) : readIds.has(notificationId(item)) ||
  unwrap(item.isRead) === true || unwrap(item.read) === true || Boolean(unwrap(item.readAt));
// Author and genre partitions can contain copies of the same release event.
// Keep original identities for read receipts and distinct release events.
export const combineReleaseCopies = (notifications) => {
  const groups = new Map();
  for (const item of new Map(notifications.map(item => [notificationId(item), item])).values()) {
    const type = unwrap(item.type), link = unwrap(item.link), title = displayLabel(unwrap(item.title));
    const time = notificationTime(item);
    const sortKey = unwrap(item.SK);
    const explicitId = unwrap(item.notificationId || item.id);
    const legacyTimestamp = typeof sortKey === 'string' && Number.isFinite(Date.parse(sortKey)) && Date.parse(sortKey) === time;
    const event = explicitId || (typeof sortKey === 'string' && sortKey.startsWith('BOOK_RELEASE#') ? sortKey : legacyTimestamp ? 'legacy-book-release' : time);
    const author = unwrap(item.authorSub || item.authorId || item.authorName);
    const key = type === 'book_release' && typeof link === 'string' && /^https?:\/\//.test(link) && title && author && time
      ? JSON.stringify(['release', link, title, author, event]) : notificationId(item);
    const copies = groups.get(key) || [];
    copies.push(item); groups.set(key, copies);
  }
  return [...groups.values()].map(copies => copies.length === 1 ? copies[0] : {...[...copies].sort((a,b) => notificationTime(b)-notificationTime(a))[0], notificationCopies: copies});
};
export const buildCircleGroups = (notifications, graph, readIds = new Set()) => {
  const unique = [...new Map(notifications.map((item) => [notificationId(item), item])).values()];
  return ["genre", "author"].flatMap((type) => {
    const follows = graph[type === "genre" ? "genres" : "authors"] || [];
    return [...new Map(follows.map((follow) => [String(follow.id).toLowerCase(), follow])).values()].map((follow) => {
      const aliases = new Set(values([follow.id, follow.name]));
      const items = unique.filter((item) => {
        const candidates = type === "genre" ? [item.genre, item.genres, item.genreId, item.category]
          : [item.authorSub, item.authorId, item.authorName, item.penName];
        return values(candidates).some((value) => aliases.has(value));
      }).sort((a, b) => notificationTime(b) - notificationTime(a));
      return { key: `${type}:${follow.id}`, type, name: follow.name || follow.id, items,
        unread: items.filter((item) => !isNotificationRead(item, readIds)).length,
        latest: items.length ? notificationTime(items[0]) : 0 };
    });
  }).sort((a, b) => Number(b.unread > 0) - Number(a.unread > 0) || b.latest - a.latest || a.name.localeCompare(b.name));
};
export const formatActivityTime = (iso) => {
  const time = Date.parse(iso);
  if (!Number.isFinite(time)) return "";
  const minutes = Math.max(0, Math.floor((Date.now() - time) / 60000));
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 1440) return `${Math.floor(minutes / 60)}h ago`;
  return new Date(time).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};
